import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { list_registrations, useEventsRequest } from './events_api';
import { RUN_STATUS_FILTERS } from './runs_table';
import { IST_ZONE } from './ist_time';

/**
 * Registrations tab — read-only, filtered by run status. "All" means every
 * registration on the topic, so the endpoint (which is per-run) is called once per
 * run in scope.
 *
 * Deliberately narrow and no export: the underlying table carries UTMs, click ids,
 * pixel cookies, IP and user agent, and the API returns only the six fields.
 */

const COLUMNS = '1.3fr 1.9fr 1.1fr 1.2fr 1fr 1.3fr';
const HEADINGS = ['Name', 'Email', 'Phone', 'Timezone', 'City', 'Registered'];
const ROW_MIN = 820;

/** Stored UTC, shown in IST, which is the frame the ops team works in. */
const asIST = (iso) => moment(iso).tz(IST_ZONE).format('D MMM YYYY, h:mm A');
const run_label = (item) => moment(item.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY');
const slot_label = (iso) => moment(iso).tz(IST_ZONE).format('h:mm A');

// RFC 4180 quoting, so a comma in a name or city cannot shift the columns.
const csv_cell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
const csv_of = (records) =>
  [HEADINGS, ...records].map((row) => row.map(csv_cell).join(',')).join('\r\n');

const download_csv = (filename, text) => {
  // Excel needs the BOM to read UTF-8, or an accented name arrives mangled.
  const url = URL.createObjectURL(new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export default function RegistrationsTab({ topic }) {
  const runs = useMemo(() => topic.runs || [], [topic.runs]);
  const [status_filter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const { run, loading } = useEventsRequest();

  const filtered_runs = useMemo(
    () => (status_filter === 'all' ? runs : runs.filter((item) => item.status === status_filter)),
    [runs, status_filter]
  );

  const load = useCallback(async () => {
    if (filtered_runs.length === 0) {
      setRows([]);
      return;
    }

    // Silent, because one toast per failed run would bury the screen.
    const results = await Promise.all(
      filtered_runs.map((item) => run(() => list_registrations(item.run_uuid), { silent: true }))
    );

    const merged = [];
    results.forEach(({ data }, index) => {
      if (!data) return;
      (data.data.registrations || []).forEach((entry) =>
        merged.push({ ...entry, run: filtered_runs[index] })
      );
    });

    merged.sort((a, b) => (a.registered_at < b.registered_at ? 1 : -1));
    setRows(merged);
  }, [run, filtered_runs]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * One group per run+slot, each carrying its own rows and its own download.
   *
   * A live slot always shows, even at zero — "nobody yet" is as often the answer a
   * host wants as a number is. A CANCELLED one shows only if somebody registered
   * before it was cancelled: §1.7 keeps cancelled slots visible so those people are
   * not hidden, and at zero there is nobody to hide.
   */
  const slot_groups = useMemo(
    () =>
      filtered_runs
        .flatMap((item) =>
          (item.slots || []).map((slot) => ({
            key: slot.slot_uuid,
            run_label: run_label(item),
            slot_label: slot_label(slot.start_utc),
            label: `${run_label(item)} ${slot_label(slot.start_utc)}`,
            cancelled: item.status === 'cancelled' || slot.status === 'cancelled',
            rows: rows.filter((entry) => entry.slot_uuid === slot.slot_uuid)
          }))
        )
        .filter((group) => !group.cancelled || group.rows.length > 0),
    [filtered_runs, rows]
  );

  const as_record = (entry) => [
    entry.name,
    entry.email,
    entry.phone || '',
    entry.user_timezone || '',
    entry.city || '',
    asIST(entry.registered_at)
  ];

  const export_slot = (group) =>
    download_csv(
      `${topic.slug}-${group.label.replace(/[^\w]+/g, '-').toLowerCase()}.csv`,
      csv_of(group.rows.map(as_record))
    );

  if (runs.length === 0) {
    return (
      <div
        className="rounded-lg p-6 text-center"
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-muted)'
        }}
      >
        No runs yet, so nobody can have registered.
      </div>
    );
  }

  return (
    <div
      className="rounded-lg"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-card)'
      }}
    >
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Registrations
          </h3>

          <select
            value={status_filter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by run status"
            className="px-2 py-1 rounded text-sm"
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)'
            }}
          >
            {RUN_STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <span className="ml-auto text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {rows.length} total across {filtered_runs.length}{' '}
            {filtered_runs.length === 1 ? 'run' : 'runs'}
          </span>
        </div>
      </div>

      {filtered_runs.length === 0 ? (
        <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
          No runs match this filter.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* One header for the table, then a band per run and slot carrying its
              own download. */}
          <div
            className="grid gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wide"
            style={{
              gridTemplateColumns: COLUMNS,
              minWidth: ROW_MIN,
              color: 'var(--text-secondary)',
              background: 'var(--surface-sunken)',
              borderBottom: '1px solid var(--border-default)'
            }}
          >
            {HEADINGS.map((heading) => (
              <div key={heading}>{heading}</div>
            ))}
          </div>

          {slot_groups.map((group) => (
            <div key={group.key}>
              <div
                className="flex items-center gap-3 px-4 py-2 text-sm"
                style={{
                  minWidth: ROW_MIN,
                  background: 'var(--surface-card)',
                  borderBottom: '1px solid var(--border-default)'
                }}
              >
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {group.run_label}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{group.slot_label}</span>
                {group.cancelled && (
                  <span className="text-xs" style={{ color: 'var(--state-error-text)' }}>
                    cancelled
                  </span>
                )}
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {group.rows.length} {group.rows.length === 1 ? 'registration' : 'registrations'}
                </span>

                <button
                  type="button"
                  className="ml-auto"
                  onClick={() => export_slot(group)}
                  disabled={group.rows.length === 0}
                  title={
                    group.rows.length === 0
                      ? 'Nobody registered for this slot'
                      : `Download ${group.rows.length} row(s) as CSV`
                  }
                  style={{
                    background: group.rows.length === 0 ? 'transparent' : 'var(--surface-sunken)',
                    border: `1px solid ${
                      group.rows.length === 0 ? 'transparent' : 'var(--border-default)'
                    }`,
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    color: group.rows.length === 0 ? 'var(--text-muted)' : 'var(--state-info)',
                    cursor: group.rows.length === 0 ? 'default' : 'pointer'
                  }}
                >
                  {group.rows.length === 0 ? '\u2014' : '\u2193 Download CSV'}
                </button>
              </div>

              {group.rows.map((entry) => (
                <div
                  key={entry.registration_uuid}
                  className="grid gap-3 px-4 py-2 text-sm"
                  style={{
                    gridTemplateColumns: COLUMNS,
                    minWidth: ROW_MIN,
                    borderBottom: '1px solid var(--border-default)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <div>{entry.name}</div>
                  <div style={{ overflowWrap: 'anywhere' }}>{entry.email}</div>
                  <div>{entry.phone || '\u2014'}</div>
                  {/* Their own IANA zone, uncoverted: it says where they are. */}
                  <div style={{ color: 'var(--text-secondary)' }}>
                    {entry.user_timezone || '\u2014'}
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>{entry.city || '\u2014'}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{asIST(entry.registered_at)}</div>
                </div>
              ))}
            </div>
          ))}

          {!loading && slot_groups.length === 0 && (
            <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
              Nothing to show — these runs have no live slots, and no cancelled slot has a
              registration.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

RegistrationsTab.propTypes = {
  topic: PropTypes.shape({
    runs: PropTypes.arrayOf(
      PropTypes.shape({
        run_uuid: PropTypes.string,
        run_date: PropTypes.string,
        status: PropTypes.string,
        slots: PropTypes.arrayOf(PropTypes.shape({ slot_uuid: PropTypes.string }))
      })
    )
  }).isRequired
};
